/**
 * Rich text editor + shared HTML sanitiser used across event description flows.
 *
 * Deliberately restricted to: Bold, Italic, Underline, Bullet list, Numbered list.
 * All other tags/attributes are stripped by DOMPurify on both save and render so
 * pasted content, malicious HTML, or extension quirks can never leak through.
 */
import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import DOMPurify from 'dompurify';
import { Bold as BoldIcon, Italic as ItalicIcon, Underline as UnderlineIcon, List, ListOrdered } from 'lucide-react';

// Tags/attrs allowed anywhere event description HTML is stored or rendered.
export const ALLOWED_TAGS = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li'];
export const ALLOWED_ATTR = [];

/** Sanitise arbitrary HTML down to the allowed subset. Safe against XSS. */
export function sanitizeDescriptionHtml(html) {
  if (!html) return '';
  return DOMPurify.sanitize(String(html), {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    KEEP_CONTENT: true,
  });
}

/** Extract plain text from possibly-HTML description content. */
export function htmlToPlainText(html) {
  if (!html) return '';
  const s = String(html);
  // If it doesn't look like HTML, return as-is (legacy plain text descriptions)
  if (!/<[a-z][\s\S]*>/i.test(s)) return s;
  // Insert whitespace at block boundaries so list items / paragraphs don't glue together
  const withBreaks = s
    .replace(/<\/(p|li|ul|ol|div|br)>/gi, ' ')
    .replace(/<br\s*\/?>/gi, ' ');
  // Strip remaining tags
  const stripped = withBreaks.replace(/<[^>]+>/g, '');
  // Decode a small set of common HTML entities
  const decoded = stripped
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
  return decoded.replace(/\s+/g, ' ').trim();
}

/** Truncate plain-text preview to `len` chars with an ellipsis when clipped. */
export function previewText(html, len = 150) {
  const t = htmlToPlainText(html);
  if (t.length <= len) return t;
  return `${t.slice(0, len).trimEnd()}…`;
}

/** Toolbar button — mirrors Shadcn button styling used elsewhere in the app. */
function ToolbarButton({ onClick, active, label, testId, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      aria-label={label}
      aria-pressed={!!active}
      title={label}
      data-testid={testId}
      className={
        'px-2 py-1 rounded-md border border-border text-sm inline-flex items-center gap-1 transition ' +
        (active
          ? 'bg-primary text-primary-foreground border-primary'
          : 'bg-card hover:bg-accent text-foreground')
      }
    >
      {children}
    </button>
  );
}

/**
 * Controlled rich-text editor.
 *
 * Props:
 *   value          Sanitised HTML string
 *   onChange(html) Called with the sanitised HTML on every doc change
 *   placeholder    Optional placeholder text (rendered via CSS)
 *   testId         Root data-testid (defaults to 'rich-text-editor')
 */
export function RichTextEditor({ value, onChange, placeholder, testId = 'rich-text-editor' }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Everything we do NOT want in the toolbar is stripped by sanitizer,
        // but disable major features here as well so keyboard shortcuts don't
        // create tags we then strip on save.
        heading: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
        strike: false,
        code: false,
      }),
      Underline,
    ],
    content: sanitizeDescriptionHtml(value || ''),
    editorProps: {
      attributes: {
        class:
          'tiptap-editor min-h-[140px] max-h-[420px] overflow-auto p-3 focus:outline-none prose prose-sm max-w-none',
        'data-testid': `${testId}-content`,
      },
    },
    onUpdate: ({ editor: ed }) => {
      const raw = ed.getHTML();
      const clean = sanitizeDescriptionHtml(raw);
      onChange?.(clean);
    },
  });

  // Keep the editor in sync when the parent replaces `value` externally
  // (e.g. after edit-mode loads existing description). We only rewrite the
  // content when the incoming value differs from what the editor already has
  // to avoid clobbering the caret while the user is typing.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = sanitizeDescriptionHtml(value || '');
    // Empty document is stored by tiptap as '<p></p>' — treat as equivalent to ''
    const norm = (v) => (v === '<p></p>' ? '' : v);
    if (norm(current) !== norm(next)) {
      editor.commands.setContent(next || '', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) {
    return (
      <div
        className="border border-border rounded-lg bg-card p-3 text-sm text-muted-foreground"
        data-testid={`${testId}-loading`}
      >
        Loading editor…
      </div>
    );
  }

  return (
    <div className="border border-border rounded-lg bg-card" data-testid={testId}>
      <div
        className="flex flex-wrap gap-1 border-b border-border p-2 bg-muted/40 rounded-t-lg"
        role="toolbar"
        aria-label="Formatting"
        data-testid={`${testId}-toolbar`}
      >
        <ToolbarButton
          label="Bold"
          testId={`${testId}-bold`}
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <BoldIcon className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Italic"
          testId={`${testId}-italic`}
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <ItalicIcon className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Underline"
          testId={`${testId}-underline`}
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon className="w-4 h-4" />
        </ToolbarButton>
        <span className="mx-1 w-px bg-border" aria-hidden="true" />
        <ToolbarButton
          label="Bullet list"
          testId={`${testId}-bullet-list`}
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Numbered list"
          testId={`${testId}-ordered-list`}
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>
      </div>
      <EditorContent editor={editor} placeholder={placeholder} />
    </div>
  );
}

/**
 * Read-only renderer for a previously-saved description HTML. Sanitises again
 * on the way out so any historic bad content is neutralised.
 */
export function RichTextView({ html, testId = 'event-description' }) {
  // Defence-in-depth: sanitise once through the shared helper, then again
  // inline via DOMPurify with the same allow-list, so a bug in either layer
  // cannot allow unsafe HTML to reach the DOM.
  const cleaned = sanitizeDescriptionHtml(html || '');
  const safe = DOMPurify.sanitize(cleaned, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    KEEP_CONTENT: true,
  });
  return (
    <div
      className="rich-text-content prose prose-sm max-w-none text-lg leading-relaxed"
      data-testid={testId}
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}
